import Color from 'color'

import { BASE_FONT_SIZE, Mode } from './constants'

export const iconSizes = {
  xxLarge: 100,
  xLarge: 60,
  large: 40,
  medium: 24,
  small: 16,
  xSmall: 12,
  xxSmall: 10,
  superscript: 8,
}

export type IconSizes = keyof typeof iconSizes

const Colors = {
  dark: '#000000', // Black
  light: '#FFFFFF', // White
  primary: '#FC3435', // Red
  secondary: '#F3F7E1', // Cream
  grey: '#494C46', // Grey
}

const baseTheme: ITheme = {
  mode: Mode.DARK,
  background: Colors.grey,
  foreground: Colors.secondary,
  text: Colors.dark,
  disabled: Colors.grey,
  primary: Colors.primary,
  secondary: Colors.secondary,
  tertiary: Colors.dark,
}

const baseLightTheme: ITheme = {
  mode: Mode.LIGHT,
  background: Colors.secondary,
  foreground: Colors.grey,
  text: Colors.light,
  disabled: Colors.grey,
  primary: Colors.primary,
  secondary: Colors.secondary,
  tertiary: Colors.dark,
}

const pxToRem = (px: number): string => `${px / BASE_FONT_SIZE}rem`

export const generateTheme = (theme: ITheme) => {
  const {
    background,
    foreground,
    text,
    disabled,
    primary,
    secondary,
    tertiary,
  } = theme
  return {
    mode: theme.mode,
    color: {
      background: background,
      foreground: foreground,
      foreground50: Color(foreground).alpha(0.5).toString(),
      interactive: foreground,
      interactiveHover: background,
      disabled,
      text,
      border: text,
      loaderBackground: Color(background).alpha(0.9).toString(),
      primary,
      primary20: Color(primary).alpha(0.2).toString(),
      secondary,
      tertiary,
      overlay: Color(background).alpha(0.9).toString(),
      white: Colors.light,
      black: Colors.dark,
      error: '#f4105a',
      inherit: 'inherit',
    },
    font: {
      weight: {
        light: 300,
        base: 400,
        medium: 500,
        bold: 600,
      },
      size: {
        xxLarge: pxToRem(36),
        xLarge: pxToRem(32),
        large: pxToRem(24),
        medium: pxToRem(18),
        base: pxToRem(16),
        small: pxToRem(14),
        xSmall: pxToRem(12),
        xxSmall: pxToRem(10),
      },
      family: {
        base: 'Futura, sans-serif',
        condensed: 'Futura-Condensed, sans-serif',
      },
    },
    transition: {
      base: 'all 280ms ease-in-out',
    },
    breakpoints: {
      small: pxToRem(300),
      medium: pxToRem(768),
      large: pxToRem(1240),
    },
    radius: {
      small: pxToRem(5),
      medium: pxToRem(10),
      large: pxToRem(16),
      full: '100%',
    },
  }
}

export const MAX_WIDTH = '1366px'

const exampleExtensionThemProps = {
  customProp: '100px',
}

export const defaultTheme = {
  ...generateTheme(baseTheme),
  ...exampleExtensionThemProps,
}

export const defaultLightTheme = {
  ...generateTheme(baseLightTheme),
  ...exampleExtensionThemProps,
}

export interface ITheme {
  mode: Mode
  background: string
  foreground: string
  text: string
  disabled: string
  primary: string
  secondary: string
  tertiary: string
}

export type ThemeInterface = ReturnType<typeof generateTheme>
export type ThemeColor = keyof ThemeInterface['color']
export type ThemeFontFamily = keyof ThemeInterface['font']['family']
export type ThemeFontWeight = keyof ThemeInterface['font']['weight']
export type ThemeFontSize = keyof ThemeInterface['font']['size']
export type ThemeBreakpoints = keyof ThemeInterface['breakpoints']
export type ThemeRadius = keyof ThemeInterface['radius']

export type ThemeInterfaceKeys =
  | `color.${ThemeColor}`
  | `font.weight.${ThemeFontWeight}`
  | `font.family.${ThemeFontFamily}`
  | `font.size.${ThemeFontSize}`
  | `breakpoints.${ThemeBreakpoints}`
  | `radius.${ThemeRadius}`
  | `transition.${keyof ThemeInterface['transition']}`

declare module 'styled-components' {
  export interface DefaultTheme extends ThemeInterface {} // extends the global DefaultTheme with our ThemeType.
}
declare module 'styled-components' {
  // Extends the global DefaultTheme with our ThemeType.
  interface DefaultTheme extends ThemeInterface {
    // customProp: string // Custom property example
  }
}
